import { useEffect, useMemo, useState } from 'react';
import { EditorModePicker } from './EditorModePicker';
import { VisibilityPicker } from './VisibilityPicker';
import { TaxonomyPicker } from './TaxonomyPicker';
import { HighlightedPicker } from './HighlightedPicker/HighlightedPicker';
import { NewExtension } from './NewExtension/NewExtension';
import { Overview } from './Overview/Overview';
import { TaggingSubModePicker } from './TaggingSubModePicker';
import { CoverageOverview } from './CoverageOverview/CoverageOverview';
import { CoverageMarkerControls } from 'app/components/editor/coverage/CoverageMarkerControls';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { useTagDisplay } from 'app/state/contexts/TagContext';
import { useTagsByDocument, useDocumentTaxonomyConfig, useExtensions } from 'entities/tag';
import { TagDataMap } from 'slate/extensions/tag/types';
import { formatTaxonomyName } from 'app/utils/tag-utils';

import './TagsToolbar.scss';
import { usePermissions } from 'app/api/permission-queries';
import { useEditorDocument } from 'app/api/documents/useEditorData';

export const TagsToolbar = () => {
  const { mode, setMode, taggingSubMode, setTaggingSubMode } = useEditorMode();
  const { highlightedOption, setHighlightedOption, setTagDataMap, visibilityOption, setVisibilityOption, setExtensionConceptIds } = useTagDisplay();
  const [selectedTaxonomyId, setSelectedTaxonomyId] = useState<string>('');

  const { data: pDocument } = useEditorDocument();
  const { documentPermissions } = usePermissions(pDocument?.superId);
  const canEditTags = documentPermissions.includes('TAG_EDITOR') || documentPermissions.includes('TAG_ADMIN');
  const { data: tagsResponse } = useTagsByDocument(pDocument?.superId, { include: ['concept', 'metadata', 'unit', 'context'] });
  const tagsData = tagsResponse?.tags;
  const { data: config } = useDocumentTaxonomyConfig(pDocument?.superId);
  const activeTaxonomyIdForExtensions = config?.taxonomySelections?.find((t) => t.isActive)?.taxonomyId ?? config?.taxonomySelections?.[0]?.taxonomyId;
  const { data: extensions } = useExtensions(pDocument?.superId, activeTaxonomyIdForExtensions);

  useEffect(() => {
    const incoming = (extensions ?? []).map((e) => e.id);
    setExtensionConceptIds((prev: Set<string>) => {
      if (prev.size === incoming.length && incoming.every((id) => prev.has(id))) return prev;
      return new Set(incoming);
    });
  }, [extensions, setExtensionConceptIds]);

  const taxonomyOptions = useMemo(() => {
    if (!config?.taxonomySelections?.length) return [];
    return config.taxonomySelections.map((ts) => ({
      label: ts.taxonomy?.name ? formatTaxonomyName(ts.taxonomy.name) : ts.taxonomyId,
      value: ts.taxonomyId,
    }));
  }, [config?.taxonomySelections]);

  // Default to the active taxonomy when config loads
  useEffect(() => {
    if (!config?.taxonomySelections?.length) return;
    const active = config.taxonomySelections.find((ts) => ts.isActive);
    if (active) {
      setSelectedTaxonomyId(active.taxonomyId);
    } else {
      setSelectedTaxonomyId(config.taxonomySelections[0].taxonomyId);
    }
  }, [config?.taxonomySelections]);

  const tagMap = useMemo(() => {
    if (!tagsData) return {};
    const map: TagDataMap = {};
    for (const tag of tagsData) {
      map[tag.id] = {
        id: tag.id,
        tagType: tag.type,
        conceptId: tag.conceptId,
        validationStatus: tag.validationStatus ?? 'unvalidated',
        status: tag.status ?? 'draft',
        nextSegment: tag.nextSegment ?? null,
        sectionSuperId: tag.sectionSuperId,
        origin: tag.origin ?? 'human',
      };
    }

    for (const tag of tagsData) {
      if (tag.nextSegment && map[tag.nextSegment]) {
        map[tag.nextSegment].predecessorId = tag.id;
      }
    }
    return map;
  }, [tagsData]);

  useEffect(() => {
    setTagDataMap(tagMap);
  }, [tagMap, setTagDataMap]);

  const onUpdateVisibility = (value: string) => {
    setVisibilityOption((prev: string[]) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  return (
    <div className="sub-toolbar tag-toolbar">
      <div className="flex flex-row gap-3">
        <EditorModePicker currentMode={mode} onModeChange={setMode} />
        {mode === 'tagging' && <TaggingSubModePicker currentValue={taggingSubMode} onChange={setTaggingSubMode} />}
      </div>
      {mode === 'tagging' && taggingSubMode === 'mark-coverage' && <CoverageMarkerControls />}

      <div className="tag-toolbar__right-section">
        <VisibilityPicker currentValue={visibilityOption} onValueChange={onUpdateVisibility} />
        <HighlightedPicker currentValue={highlightedOption} onValueChange={setHighlightedOption} />
        <TaxonomyPicker currentValue={selectedTaxonomyId} options={taxonomyOptions} onValueChange={setSelectedTaxonomyId} />

        {canEditTags && pDocument?.superId && selectedTaxonomyId && <NewExtension documentSuperId={pDocument.superId} taxonomyId={selectedTaxonomyId} />}

        <Overview />
        <CoverageOverview />
      </div>
    </div>
  );
};
